// Section service for calling public section API endpoints
import { apiClient } from './api-client';
import type {
  SectionWithGames,
  SectionType
} from '@/types/section';
import type { ApiError } from './api-client';

export class SectionService {
  private readonly maxRetries = 3;
  private readonly retryDelayMs = 1000;

  /**
   * Retry logic with exponential backoff
   */
  private async withRetry<T>(
    operation: () => Promise<T>,
    retries = this.maxRetries,
    delay = this.retryDelayMs
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (retries > 0) {
        // Check if it's a retryable error (network errors, 5xx, rate limiting)
        const apiError = error as ApiError;
        const isRetryable = 
          !apiError.status || // Network error
          apiError.status >= 500 || // Server error
          apiError.status === 429; // Rate limited

        if (isRetryable) {
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.withRetry(operation, retries - 1, delay * 2); // Exponential backoff
        }
      }
      throw error;
    }
  }

  /**
   * Get all active sections with associated games
   */
  async getSections(): Promise<SectionWithGames[]> {
    return this.withRetry(async () => {
      try {
        const response = await apiClient.getSections();

        // Validate response data
        if (!Array.isArray(response)) {
          throw new Error('Invalid response format: expected sections array');
        }

        // Validate each section
        const validSections = response.filter(section => {
          return this.validateSectionData(section);
        });

        return validSections;
      } catch (error) {
        throw error;
      }
    });
  }

  /**
   * Get a specific section by ID with associated games
   */
  async getSectionById(sectionId: number): Promise<SectionWithGames> {
    // Validate input immediately (no retry needed for validation errors)
    if (!sectionId || sectionId <= 0) {
      throw {
        message: 'Invalid section ID',
        status: 400,
        error: 'INVALID_SECTION_ID'
      } as ApiError;
    }

    return this.withRetry(async () => {
      try {
        const response = await apiClient.getSectionById(sectionId);

        // Validate response data
        if (!this.validateSectionData(response)) {
          throw new Error('Invalid section data received from API');
        }

        return response;
      } catch (error) {
        throw error;
      }
    });
  }

  /**
   * Determine section type based on section name for special handling
   */
  getSectionType(section: SectionWithGames): SectionType {
    switch (section.name) {
      case 'cta_premium':
        return 'cta_premium';
      case 'coming_soon':
        return 'coming_soon';
      default:
        return 'regular';
    }
  }

  /**
   * Filter sections to only return active ones, sorted by sortOrder
   */
  filterActiveSections(sections: SectionWithGames[]): SectionWithGames[] {
    return sections
      .filter(section => section.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * Validate section data structure
   */
  validateSectionData(section: unknown): section is SectionWithGames {
    if (!section || typeof section !== 'object') {
      return false;
    }

    const s = section as SectionWithGames;
    
    return (
      typeof s.id === 'number' &&
      typeof s.name === 'string' &&
      typeof s.displayName === 'string' &&
      (s.description === null || typeof s.description === 'string') &&
      typeof s.sortOrder === 'number' &&
      typeof s.isActive === 'boolean' &&
      Array.isArray(s.games)
    );
  }

  /**
   * Handle section service errors with appropriate fallback
   */
  handleSectionError(_error: unknown, fallbackSections: SectionWithGames[] = []): SectionWithGames[] {
    // Return empty array as fallback
    return fallbackSections;
  }
}

// Export singleton instance
export const sectionService = new SectionService();

// Export for use in other components
export default sectionService;